import appstate from "../appstate";

interface ICalendarPeriod {
    startYear: number,
    startMonth: number,
    duration: number,
    calendarResponse: NCB.Calendar.ExtendedCalendarResponse[],
    guestCount: CC.Library.Models.Calendar.NcbCalendarRoom[], 
    promotionCode: string,
    ratePlanOrGroupCode: string
}

export enum IEndDateStatus {
    Loading,
    Available,
    Unavailable
}

class CalendarStore {
    private _hintStartDate: Date = null;

    public calendarResponse: CC.Library.Models.Calendar.CalendarResponse;
    public warnings: CC.Library.Models.Shared.IWarningItem[];
    public promotionCode: string;
    public ratePlanOrGroupCode: string;
    public isPromotionCodeValid: boolean = false;
    public isGroupCodeValid: boolean = false;
    public hasError: boolean = false;
    public loadCallback: () => void = null;
    public loadPeriodCallback: () => void = null;    
    public foundFirstAvailablePeriod: boolean = false;

    calendarPeriods: ICalendarPeriod[];

    private XHRReference: XMLHttpRequest;

    constructor() {
        this.calendarPeriods = [];
    }

    public set hintStartDate(date: Date) {
        this._hintStartDate = date;
        console.log("set hintStartDate", date);
        this.loadCalendarPeriods();
    }

    public get hintStartDate() {
        return this._hintStartDate;
    }

    public getCalendarPeriod(numNights: number): ICalendarPeriod {
        let foundPeriods = this.calendarPeriods.filter(x => x.duration === numNights);

        if (foundPeriods.length > 0) 
            return foundPeriods[0];

        return null;
    }

    public abortAllRequests = (): void => {
        if (this.XHRReference != null)
            this.XHRReference.abort();
    }

    createCalendarPeriods(startYear: number, startMonth: number, guestCount: CC.Library.Models.Calendar.NcbCalendarRoom[], promotionCode: string, groupCode: string, loadPeriodCallback: () => void) {
        this.abortAllRequests();
        
        this.calendarPeriods = [];

        // Add the first period which already is fetched
        let firstPeriodDummy = NCB.Calendar.createDummyResponses(1, startYear, startMonth);

        firstPeriodDummy[0] = this.calendarResponse as NCB.Calendar.ExtendedCalendarResponse;

        // Add dummy months to the first period
        firstPeriodDummy.forEach((p, i) => {
            if (i > 0) {
                this.calendarResponse.Availability = this.calendarResponse.Availability.concat(p.Availability);
                this.calendarResponse.NumMonths++;
            }
        });

        this.calendarPeriods.push({
            duration: 1,
            calendarResponse: firstPeriodDummy, 
            guestCount: guestCount,
            promotionCode: promotionCode,
            ratePlanOrGroupCode: groupCode,
            startYear: startYear,
            startMonth: startMonth
        });

        // console.log("*** createCalendarPeriods startYear, startMonth", startYear, startMonth);

        for (let i=2; NCB.Settings.CalendarMaxDurationDays >= i; i++) {
            this.calendarPeriods.push({
                duration: i,
                calendarResponse: NCB.Calendar.createDummyResponses(i, startYear, startMonth), 
                guestCount: guestCount,
                promotionCode: promotionCode,
                ratePlanOrGroupCode: groupCode,
                startYear: startYear,
                startMonth: startMonth
            });
        }

        this.calendarPeriods = this.calendarPeriods.sort((a, b) => {
            return a.duration < b.duration ? -1 : 1;
        });

        // console.log("*** Initial Periods", JSON.parse(JSON.stringify(this.calendarPeriods)));

        this.loadCalendarPeriods();
    }

    loadCalendarPeriods = () => {

        if (this._hintStartDate == null) {
            console.log("Skip loadCalendarPeriods beacause hintStartDate is null");
            return;
        }
         
        let selectedStartYear = this._hintStartDate.getUTCFullYear();
        let selectedStartMonth = this._hintStartDate.getUTCMonth() + 1;

        // Find a period response that is not yet loaded
        let calendarPeriod: ICalendarPeriod;
        let calendarPeriodResponse: NCB.Calendar.ExtendedCalendarResponse = null;
        this.calendarPeriods.forEach(period => {
            if (calendarPeriod == null || calendarPeriodResponse == null) {
                let response = period.calendarResponse.filter(x => x.IsDummy && x.StartYear == selectedStartYear && x.StartMonth == selectedStartMonth);
                if (calendarPeriodResponse == null && response.length > 0) {
                    calendarPeriod = period;
                    calendarPeriodResponse = response[0];
                }
            }
        });

        if (calendarPeriodResponse == null) {
            console.log("loadCalendarPeriods: No more periods for hintStartDate was found", this._hintStartDate);
            return;
        }
            
        let req = NCB.Calendar.generateDefaultRequest();
        req.StartYear = calendarPeriodResponse.StartYear;
        req.StartMonth = calendarPeriodResponse.StartMonth;
        req.PromotionCode = calendarPeriod.promotionCode;
        req.RatePlanOrGroupCode = calendarPeriod.ratePlanOrGroupCode;
        req.Duration = calendarPeriod.duration;
        req.Rooms = calendarPeriod.guestCount;
        req.NumMonths = 1;
        req.PreheldInventoryId = (appstate != null) ? appstate.modifyConfirmationNumber : "";

        this.XHRReference = NCB.Calendar.getCalendar({
            success: (res: CC.Library.Models.Calendar.CalendarResponse, warnings: CC.Library.Models.Shared.IWarningItem[]) => {
                
                let res2 = (res as NCB.Calendar.ExtendedCalendarResponse);
                res2.IsDummy = false;

                // Replace the dummy month
                calendarPeriodResponse.Availability = res2.Availability;
                calendarPeriodResponse.IsDummy = false;
                calendarPeriodResponse.Success = res2.Success;

                
                if (calendarPeriod.duration === 1) {
                    // Add to the result if this is the first period
                    res.Availability.forEach(a => {
                        let t = this.calendarResponse.Availability.filter(c => c.Date === a.Date);
                        if (t.length > 0) {
                            t[0].AmountAfterTax = a.AmountAfterTax;
                            t[0].AmountBeforeTax = a.AmountBeforeTax;
                            t[0].Quantity = a.Quantity;
                            t[0].Status = a.Status;
                        }
                    });
                } else {
                    // NHRSUPPORT-3107 
                    // In the case that 1-night stay is unavailable, check if a longer stay is available
                    res.Availability.forEach(a => {
                        let t = this.calendarResponse.Availability.filter(c => c.Status == CC.Library.Models.Calendar.AvailabilityStatus.Closed && c.Date === a.Date)[0];
                        if (t != null) {
                            if (a.Status != CC.Library.Models.Calendar.AvailabilityStatus.Closed) {
                                // console.log("--- ALTERNATE PERIOD (date, from, to)", a.Date, t.Status, a.Status);
                                let calculatedPrice = (parseInt(a.AmountAfterTax) / calendarPeriod.duration).toString();
                                // console.log("--- ALTERNATE PRICE (duration, from, to, calculatedPrice)", calendarPeriod.duration, t.AmountAfterTax, a.AmountAfterTax, calculatedPrice);
                                t.Status = CC.Library.Models.Calendar.AvailabilityStatus.Open;
                                t.AmountAfterTax = calculatedPrice;
                            }
                        }
                    });
                }

                // console.log("Load period from hint (year, month, duration)", res2.StartYear, res2.StartMonth, req.Duration);

                if (this.loadCallback != null)
                    this.loadCallback();

                if (this.loadPeriodCallback != null)
                    this.loadPeriodCallback();

                // Recursive loop
                this.loadCalendarPeriods();
            },
            error: (error: NCB.Proxy.ICompoundError) => {
                NCB.Log.customError("Could not load calendar period", {startYear: selectedStartYear, startMonth: selectedStartMonth, error: error});
                this.hasError = true;
            }
        }, req, false);
    }

    loadCalendar (startYear: number, startMonth: number, guestCount: CC.Library.Models.Calendar.NcbCalendarRoom[], promotionCode: string, ratePlanOrGroupCode: string, callback: () => void, roomTypeFilter: string, loadPeriodCallback: () => void) {
        NCB.Calendar.abort();
        this.foundFirstAvailablePeriod = false;

        if (this.XHRReference != null) {
            this.XHRReference.abort();
        }

        let req = NCB.Calendar.generateDefaultRequest();

        req.StartYear = startYear; // NCB.Settings.ForceStartYear || null;
        req.StartMonth = startMonth; // NCB.Settings.ForceStartMonth || null;
        req.PromotionCode = promotionCode;
        req.RatePlanOrGroupCode = ratePlanOrGroupCode,
        req.Duration = 1; // Always use 1 night by default
        req.RoomTypeCode = roomTypeFilter;
        req.NumMonths = 1;
        req.PreheldInventoryId = (appstate != null) ? appstate.modifyConfirmationNumber : "";

        req.Rooms = guestCount;

        this.XHRReference = NCB.Calendar.getCalendar({
            success: (res: CC.Library.Models.Calendar.CalendarResponse, warnings: CC.Library.Models.Shared.IWarningItem[]) => {

                // NCB.Calendar.removeEmptyMonths(res);

                this.calendarResponse = res;
                this.hasError = false;
                
                this.isPromotionCodeValid = true;
                this.isGroupCodeValid = true;
        
                if (warnings != null && warnings.length > 0) {
                    warnings.forEach((x) => {
                        if (parseInt(x.Code) === CC.Library.Helpers.NcbWarningCodes.INVALID_PROMOTION_CODE) {
                            this.isPromotionCodeValid = false;
                        } else if (parseInt(x.Code) === CC.Library.Helpers.NcbWarningCodes.INVALID_RATE_CODE) {
                            this.isGroupCodeValid = false;
                        } else {
                            NCB.Log.error(new Error("CustomError"), "Unhandled warning in Datepicker.loadCalendarCallback", {warning: x});
                        }
                    });
                }
                
                this.promotionCode = this.isPromotionCodeValid ? promotionCode : "";

                this.isGroupCodeValid = this.isGroupCodeValid;
                this.ratePlanOrGroupCode = this.isGroupCodeValid ? ratePlanOrGroupCode : "";
                
                // Only trigger the callback if a valid period was found
                if (appstate.useAlternativeHotel || res.Availability.filter(x => x.Status > 0).length > 0) {
                    
                    if (this._hintStartDate == null) {
                        this._hintStartDate = new Date(res.StartYear, res.StartMonth -1, 1);
                        console.log("set hintStartDate AUTO", this._hintStartDate);
                    }
                        
                    this.createCalendarPeriods(res.StartYear, res.StartMonth, guestCount, this.isPromotionCodeValid ? promotionCode : null, this.isGroupCodeValid ? ratePlanOrGroupCode : null, loadPeriodCallback);
                    this.foundFirstAvailablePeriod = true;
                    callback();
                } else {

                    // No hits for the first month, try next
                    if (startYear == null || startMonth == null) {
                        let now = new Date();
                        startYear = now.getUTCFullYear();
                        startMonth = now.getUTCMonth();
                    }
                    let d = new Date(Date.UTC(startYear, startMonth, 1));

                    this.loadCalendar(d.getUTCFullYear(), d.getUTCMonth() + 1, guestCount, this.promotionCode, this.ratePlanOrGroupCode, callback, roomTypeFilter, loadPeriodCallback);
                }

            },
            error: (error: NCB.Proxy.ICompoundError) => {
                NCB.Log.customError("Could not load calendar ", {guestCount: guestCount, promotionCode: promotionCode, error: error});
                this.hasError = true;
            }
        }, req);
    }

    loadCalendarMonths = (req: CC.Library.Models.Calendar.CalendarRequest) => {
        
    }

    isEndDateAvailable(startDate: Date, checkEndDate: Date): IEndDateStatus {
        let numNights = NCB.DateUtils.getNumNights(startDate, checkEndDate);

        let currentStartDate = NCB.DateUtils.getISODate(startDate);

        if (numNights < 1)
            return IEndDateStatus.Unavailable;

        if (numNights > NCB.Settings.CalendarMaxDurationDays) {
            return IEndDateStatus.Unavailable;
        }

        let currentCalendar = this.getCalendarPeriod(numNights);
        if (currentCalendar == null || currentCalendar.calendarResponse == null) {
            // console.warn("NO CALENDAR FOUND FOR END DATE " + checkEndDate);
            return IEndDateStatus.Loading;
        }

        let currentDay:CC.Library.Models.Calendar.CalendarDay = null;
        currentCalendar.calendarResponse.forEach(x => {
            let d = x.Availability.filter(x => x.Date === currentStartDate);
            if (d.length > 0)
                currentDay = d[0];
        });
    
        if (currentDay != null) {
            switch (currentDay.Status) {
                case CC.Library.Models.Calendar.AvailabilityStatus.Closed:
                    return IEndDateStatus.Unavailable

                case CC.Library.Models.Calendar.AvailabilityStatus.Open:
                    return IEndDateStatus.Available;

                case CC.Library.Models.Calendar.AvailabilityStatus.ClosedOnArrival:
                    return IEndDateStatus.Unavailable;

                case CC.Library.Models.Calendar.AvailabilityStatus.MinStay:
                    if (parseInt(currentDay.Quantity) > currentCalendar.duration) {
                        return IEndDateStatus.Unavailable;
                    } else { 
                        return IEndDateStatus.Available;
                    }

                case CC.Library.Models.Calendar.AvailabilityStatus.MaxStay:
                    if (parseInt(currentDay.Quantity) < currentCalendar.duration) {
                        return IEndDateStatus.Unavailable;
                    } else { 
                        return IEndDateStatus.Available;
                    }
            }
            
        } else {
            return IEndDateStatus.Unavailable;
        }
    }

    isFirstPeriodLoaded = (): boolean => {
        return this.foundFirstAvailablePeriod;
    }

    /**
     * Check if the requested date is loaded in the first period (duration===1)
     */
    isDateLoaded = (date: string): boolean => {
        let periods = this.calendarPeriods.filter(p => p.duration === 1).map(x => x.calendarResponse);
        let i, k, s: number;
        for (let i=0; periods.length > i; i++) {
            for (let k=0; periods[i].length > k; k++) {
                if (periods[i][k].IsDummy !== true) {
                    if (periods[i][k].Availability.filter(x => x.Date === date).length > 0) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    /**
     * @returns boolean or null if unknown
     */
    isDateBookable  = (startDate: Date, endDate: Date): boolean | null => {
        let duration = NCB.DateUtils.getNumNights(startDate, endDate);
        let startDateISO = NCB.DateUtils.getISODate(startDate);

        //console.log("***isDateBookable duration:", duration);
        //console.log("***isDateBookable startDateISO:", startDateISO);

        let durationCalendar = this.calendarPeriods.filter(x => x.duration === duration)[0];
        if (durationCalendar == null || durationCalendar.calendarResponse == null) {
            //console.log("***isDateBookable durationCalendar.calendarResponse is null");
            return null;
        }

        for (let i=0; durationCalendar.calendarResponse.length > i; i++) {
            if (durationCalendar.calendarResponse[i].Availability == null)
                return null;

            for (let j=0; durationCalendar.calendarResponse[i].Availability.length > j; j++) {
                if (durationCalendar.calendarResponse[i].Availability[j] != null && durationCalendar.calendarResponse[i].Availability[j].Date === startDateISO) {
                    //console.log("***isDateBookable found match in index", i, durationCalendar.calendarResponse[i].Availability[j]);
                    return durationCalendar.calendarResponse[i].Availability[j].Status !== CC.Library.Models.Calendar.AvailabilityStatus.Closed;
                }
            }
        }

        return null;
    }

    /**
     * @returns ISO Date
     */
    getFirstBookableDate = (): string | null => {
        let durationCalendar = this.calendarPeriods.filter(x => x.duration === 1);
        for (let i=0; durationCalendar.length > i; i++) {
            for (let j=0; durationCalendar[i].calendarResponse.length > j; j++) {
                for (let k=0; durationCalendar[i].calendarResponse[j].Availability.length > k; k++) {
                    if (durationCalendar[i].calendarResponse[j].Availability[k].Status !== CC.Library.Models.Calendar.AvailabilityStatus.Closed)
                        return durationCalendar[i].calendarResponse[j].Availability[k].Date;
                }
            }
        }
        
        return null;
    }
}

const calendarStore = new CalendarStore();

export default calendarStore;



// WEBPACK FOOTER //
// ./src/stores/calendarStore.ts